/* cspell:ignore nics */
import type { ContainerVolumeStatus } from '@/delivery-type-plugin/capabilities/container-storage';
import type { NicConfigurationValue } from '@/types/nic-configuration';

export const MAAS_GROUP = 'core.maas.stack.io';

/** 后端 MaasWorkload 类型标签 */
export const MAAS_WORKLOAD_FRAMEWORK_LABEL = 'maasworkloads.core.maas.stack.io/framework';
export const MAAS_WORKLOAD_RUN_MODE_LABEL = 'maasworkloads.core.maas.stack.io/run-mode';

/** 推理框架（与后端 MaasFramework 枚举一致） */
export enum MaasFramework {
  VLLM = 'vLLM',
  SGlang = 'SGlang',
}

/** 部署拓扑（与后端 RunMode 枚举一致） */
export enum MaasRunMode {
  /** 前端 UI 合并模式：覆盖旧 Single/Distributed 两种形态 */
  Normal = 'Normal',
  Single = 'Single',
  Distributed = 'Distributed',
  PrimarySecondary = 'PrimarySecondary',
}

export interface MaasCondition {
  type?: string;
  reason?: string;
  lastTransitionTime?: string;
  status?: string;
  message?: string;
}

export enum MaasMode {
  Provisioned = 'Provisioned',
  OnDemand = 'OnDemand',
}

export enum MaasRuntimeType {
  ContainerInstance = 'ContainerInstance',
  Ray = 'Ray',
}

export enum MaasWorkloadType {
  ContainerInstance = 'ContainerInstance',
  RayJob = 'RayJob',
  RayService = 'RayService',
}

export enum MaasModelSourceType {
  Http = 'http',
  S3 = 's3',
  ModelScope = 'modelscope',
  HuggingFace = 'huggingface',
  StackScope = 'stackscope',
  ModelPlaza = 'modelplaza',
}

/** 推理框架来源：平台内置 / 自定义（仅 UI 展示，不提交 API） */
export enum MaasInferenceSource {
  Builtin = 'builtin',
  Custom = 'custom',
}

/** 表单字段：推理框架来源（跨步骤保持，避免切步后被 system 反推覆盖） */
export const MAAS_INFERENCE_SOURCE_FIELD = 'maasInferenceSource';

/** 顶层 / 组内服务项（API 字段 usage；表单侧仍用 usageScenario） */
export interface MaasNetworkServiceItem {
  name?: string;
  protocol?: string;
  internalPort?: number;
  externalPort?: number;
  description?: string;
  routerPath?: string;
  applicationProtocol?: string;
  /** 使用场景（API 持久化字段） */
  usage?: string;
  /** 服务挂载的节点组名称 */
  groupNames?: string[];
  llmGWModelBindingTemplate?: Record<string, unknown>;
}

export interface MaasNetwork {
  vpc?: string;
  securitygroup?: string;
  /** 顶层服务列表（模型实例：不再写入各组 resource.network.service） */
  service?: MaasNetworkServiceItem[];
  /** 通过公共网卡组件选择的 NIC 资源。 */
  nics?: NicConfigurationValue;
}

/** 顶层环境变量（模型实例：不再写入各组 system.env） */
export interface MaasEnvironment {
  env?: { name: string; value: string; description?: string }[];
}

/** 制品 accessConfig.apis 项（与 Endpoint 配置一致） */
export interface MaasServiceInfo {
  name: string;
  /** 使用场景 */
  usageScenario?: string;
  protocol: string;
  internalPort: number;
  port: number;
  description: string;
  tranProtocol: string;
}

/** 创建表单服务端口项（对应 stack-maas ResourceServiceInfomation） */
export interface MaasInferenceServiceFormItem {
  name?: string;
  /** 使用场景（表单字段；提交映射为 network.service[].usage） */
  usageScenario?: string;
  /** 传输协议（K8s protocol：TCP/UDP/SCTP） */
  protocol?: string;
  internalPort?: number;
  externalPort?: number;
  description?: string;
  routerPath?: string;
  applicationProtocol?: string;
  /** 制品预填项，UI 只读标记 */
  _isDefault?: boolean;
}

export interface MaasRuntime {
  type: MaasRuntimeType;
  workload: MaasWorkloadType;
}

export interface MaasModelSourceHTTP {
  url: string;
}

export interface MaasModelSourceS3Secret {
  accessKeyID: string;
  secretKey: string;
}

export interface MaasModelSourceS3 {
  url: string;
  secretRef?: MaasModelSourceS3Secret;
}

export interface MaasModelSourceModelScope {
  model: string;
}

export interface MaasModelSourceHuggingface {
  model: string;
}

/** 与后端 MaaSModelSourceStackScope 一致 */
export interface MaasModelSourceStackScope {
  model: string;
  url: string;
}

/** 与后端 MaaSModelSourceModelPlaza 一致 */
export interface MaasModelSourceModelPlaza {
  model: string;
  url: string;
}

export interface MaasModelSource {
  http?: MaasModelSourceHTTP;
  s3?: MaasModelSourceS3;
  modelscope?: MaasModelSourceModelScope;
  huggingface?: MaasModelSourceHuggingface;
  stackscope?: MaasModelSourceStackScope;
  modelplaza?: MaasModelSourceModelPlaza;
}

export interface MaasResourceCPU {
  vendor?: string;
  device?: string;
  deviceType?: string;
  vendorID?: string;
  deviceID?: string;
  cores?: string | number;
  threads?: string | number;
  sockets?: string | number;
  frequency?: string;
  resource?: Record<string, { request?: string | number; limit?: string | number }>;
}

export interface MaasResourceMemory {
  vendor?: string;
  device?: string;
  deviceType?: string;
  vendorID?: string;
  deviceID?: string;
  resource?: Record<string, { request?: string | number; limit?: string | number }>;
}

export interface MaasResourceGPU {
  resourceType?: string;
  vendor?: string;
  device?: string;
  deviceType?: string;
  vendorID?: string;
  deviceID?: string;
  driverVersion?: string;
  cudaVersion?: string;
  memory?: string | number;
  resource?: Record<string, { request?: string | number; limit?: string | number }>;
}

export interface MaasResourceNetwork {
  subnet?: string;
  dnsConfig?: Record<string, unknown>;
  default?: Record<string, unknown>;
  service?: Record<string, unknown>[];
  nic?: Record<string, unknown>[];
}

export interface MaasResourceStorage {
  device?: Record<string, unknown>[];
  volume?: Record<string, unknown>[];
}

export interface MaasResource {
  cpu?: MaasResourceCPU;
  memory?: MaasResourceMemory;
  gpu?: MaasResourceGPU;
  storage?: MaasResourceStorage;
  /** 节点组可调度网卡（模型实例 embedded 节点） */
  nics?: NicConfigurationValue;
  network?: MaasResourceNetwork;
}

/** 组在 headGroupSpec / workerGroupSpecs 中的角色（表单内部区分用） */
export enum MaasGroupRoleType {
  Master = 'Master',
  Worker = 'Worker',
}

/** 任务步骤运行策略 */
export type MaasTaskRunStrategy = 'Parallel' | 'Sequential' | 'Single' | 'Stepwise';
export type MaasTaskFailurePolicy = 'Terminate' | 'Skip' | 'Pause';
export type MaasTaskRole = 'All' | 'Head' | 'Worker';

/** 任务模板步骤（与 API taskTemplate 对齐） */
export interface MaasTaskTemplateStep {
  name: string;
  role: MaasTaskRole;
  groups?: string[];
  command: MaasInitCommand;
  runStrategy: MaasTaskRunStrategy;
  failurePolicy: MaasTaskFailurePolicy;
  /** 是否异步执行，默认 true */
  async?: boolean;
  /** 依赖的前置任务名称列表 */
  dependsOn?: string[];
}

export interface MaasInitCommand {
  commands: string[];
  args?: string[];
  /** 任务步骤 command 超时（秒） */
  timeout?: number;
  /** 任务步骤 command 重试次数 */
  retryCount?: number;
}

export interface MaasGroupSpec {
  env?: { name: string; value: string; description?: string }[];
  name: string;
  replicas?: number;
  system?: Record<string, unknown>;
  resource?: MaasResource;
  /** 节点级初始化命令 */
  initCommand?: MaasInitCommand;
  command?: string[];
  args?: string[];
  /** 镜像仓库账密，提交时写入各组 system.registry */
  registry?: { server?: string; username?: string; password?: string };
}

/** 含 head/worker 组划分的 Spec 片段（展示与解析用） */
export interface MaasGroupedWorkloadSpec {
  datacenter?: string;
  workspace?: string;
  alias?: string;
  description?: string;
  creator?: string;
  mode?: MaasMode;
  framework?: MaasFramework;
  runMode?: MaasRunMode;
  runtime?: MaasRuntime;
  network?: MaasNetwork;
  /** 顶层环境变量（模型实例持久化） */
  environment?: MaasEnvironment;
  headGroupSpec?: MaasGroupSpec;
  workerGroupSpecs?: MaasGroupSpec[];
  /** 网关节点组（单组，无部署模式） */
  gateway?: MaasGroupSpec;
  /** 顶层期望副本数（实例级） */
  replicas?: number;
  /** 全局初始化命令（对所有节点生效） */
  initCommand?: MaasInitCommand;
  /** 任务模板 */
  taskTemplate?: MaasTaskTemplateStep[];
  /** spec.annotations，模型实例写入 stack.io/maasForm.containerDeployConfig；应用实例写入完整 maasForm 快照 */
  annotations?: Record<string, string>;
}

export interface MaasNetworkStatus {
  internalIPs?: string[];
  service?: Record<string, unknown>[];
}

export interface MaasProvisioningStatus {
  progress?: string | number;
}

/** 模型实例按 component 拓扑的副本与运行状态（status.componentStatuses） */
export interface MaasComponentStatus {
  component?: string;
  desiredReplicas?: number;
  currentReplicas?: number;
  readyReplicas?: number;
  phase?: string;
  observedGeneration?: number;
}

export interface MaasWorkloadStatus {
  conditions?: MaasCondition[];
  phase?: string;
  ready?: boolean;
  /** 当前实际副本数（部分 Workload 仍使用；模型实例副本展示见 componentStatuses） */
  replicas?: number;
  /** 模型实例：按 component 的副本与 phase */
  componentStatuses?: MaasComponentStatus[];
  network?: MaasNetworkStatus;
  provisioningStatus?: MaasProvisioningStatus;
  /** 卷运行状态（详情接口返回，含 storageClass alias/name/provisioner） */
  volumeStatus?: ContainerVolumeStatus[];
}

export interface MaasWorkload {
  apiVersion?: string;
  kind?: string;
  metadata: {
    name: string;
    namespace: string;
    creationTimestamp?: string;
    labels?: Record<string, string>;
    annotations?: Record<string, string>;
  };
  spec: MaasGroupedWorkloadSpec;
  status?: MaasWorkloadStatus;
}

export interface MaasWorkloadListResult {
  items: MaasWorkload[];
  totalItems: number;
}

export interface MaasRemoteOptions {
  host?: string;
}

export interface MaasOptions {
  mode?: string[];
  runMode?: string[];
  runtime?: Record<string, string[]>;
  model?: { source?: string[] };
  /** 远程访问网关（/types 接口返回） */
  remote?: MaasRemoteOptions;
}

/** 模型/应用实例创建表单共用 UI 字段（组 JSON、卷、镜像配置等，不含 API 组 spec） */
export interface MaasSharedFormValues extends Omit<MaasGroupedWorkloadSpec, 'initCommand' | 'taskTemplate'> {
  /** UI：主组节点 JSON */
  masterNodes?: string;
  /** UI：从组节点 JSON */
  workerNodes?: string;
  /** UI：网关节点 JSON */
  gatewayNodes?: string;
  /** UI：网关系统盘 Gi */
  'gateway.systemDiskSize'?: number;
  /** UI：网关系统盘计价选型 */
  gatewaySystemDiskPricing?: Record<string, unknown>;
  /** UI：推理/应用镜像 system（hidden 字段存 JSON 字符串） */
  _inferenceSystem?: Record<string, unknown> | string;
  /** UI：服务端口（hidden 字段存 JSON 字符串） */
  _inferenceService?: unknown | string;
  /** UI：服务入口节点组名称（多选，列表共用） */
  _serviceMountGroupName?: string | string[];
  /** 主组 Data 卷（hidden 字段存 JSON 字符串，提交前 parsePrimaryDataVolumeField 解析） */
  _primaryDataVolume?: string;
  /** UI：主组系统盘 Gi */
  'master.systemDiskSize'?: number;
  /** UI：Worker 系统盘 Gi */
  'worker.systemDiskSize'?: number;
  /** UI：主组系统盘计价选型 */
  masterSystemDiskPricing?: Record<string, unknown>;
  /** UI：Worker 系统盘计价选型 */
  workerSystemDiskPricing?: Record<string, unknown>;
  /** UI：推理框架镜像，json 字符串 */
  inferenceFrameworkImage?: string;
  /** UI：镜像 containerDeployConfig 挂载根路径 */
  _imageContainerDeployMountPath?: string;
  /** UI：镜像 containerDeployConfig.startupCommands（AppInstance hidden 字段存 JSON 字符串） */
  _imageContainerDeployStartup?: Record<string, unknown> | string;
  /** UI：镜像 containerDeployConfig.instanceScheduling（hidden 字段存 JSON 字符串） */
  _imageContainerDeployInstanceScheduling?: Record<string, unknown> | string;
  /** UI：镜像 containerDeployConfig.envVars */
  _imageContainerDeployEnvVars?: { name: string; value: string; description?: string }[];
  /** UI：镜像 containerDeployConfig.apis */
  _imageContainerDeployApis?: { name: string; value: string; description?: string }[];
  /** UI：镜像 containerDeployConfig.healthCheck（探活模块编辑真源） */
  _containerDeployHealthCheck?: Record<string, unknown>;
  /** UI：公共环境变量 */
  commonEnv?: { name: string; value: string; description?: string }[];
  /** UI：镜像 containerDeployConfig 存储卷（hidden JSON，模型实例提交时写入 volume） */
  _imageStorageVolumes?: string;
  /** UI：任务模板 JSON */
  taskTemplate?: string | MaasTaskTemplateStep[];
  /** UI：全局初始化命令辅助字段（initCommand._shellType 等，不提交 API） */
  initCommand?: MaasInitCommand & {
    _shellType?: 'bash' | 'sh';
    _commandsText?: string;
    _argsText?: string;
  };
  /** UI：SKU/直建模块勾选状态 */
  skuResourceModules?: Record<string, boolean>;
}

/** 创建/编辑表单基类：K8s 命名空间 + 推理/镜像来源 */
export interface MaasInstanceFormValues extends MaasSharedFormValues {
  namespace?: string;
  maasInferenceSource?: MaasInferenceSource;
}

/** 提交前校验错误（i18n key + 可选字段名与插值参数） */
export interface MaasSubmitError {
  key: string;
  fieldKey?: string;
  params?: Record<string, string | number>;
  /** 已 toast，调用方不要再 Message.error */
  alreadyNotified?: boolean;
}

/** 节点组系统盘大小 UI 缓存（key 为组字段路径或 workers 共用） */
export interface MaasSystemDiskSizes {
  headGroupSpec?: number;
  'workerGroupSpecs[0]'?: number;
  /** 分布式/主从下 Worker 节点共用系统盘大小 */
  workers?: number;
}

export const DEFAULT_MAAS_OPTIONS: MaasOptions = {
  mode: [MaasMode.Provisioned, MaasMode.OnDemand],
  runMode: [MaasRunMode.Single, MaasRunMode.Distributed, MaasRunMode.PrimarySecondary],
  runtime: {
    [MaasRuntimeType.ContainerInstance]: [MaasWorkloadType.ContainerInstance],
    [MaasRuntimeType.Ray]: [MaasWorkloadType.RayJob, MaasWorkloadType.RayService],
  },
  model: { source: ['HTTP', 'S3', 'ModelScope', 'HuggingFace', 'StackScope', 'ModelPlaza'] },
};

/** 后端 MaaSRuntimeType 合法值（stack-maas/api/core/v1/types.go） */
const MAAS_RUNTIME_TYPE_CANONICAL: Record<string, MaasRuntimeType> = {
  [MaasRuntimeType.ContainerInstance]: MaasRuntimeType.ContainerInstance,
  [MaasRuntimeType.Ray]: MaasRuntimeType.Ray,
  containerinstance: MaasRuntimeType.ContainerInstance,
  ray: MaasRuntimeType.Ray,
};

/** 后端 MaaSWorkloadType 合法值 */
const MAAS_WORKLOAD_TYPE_CANONICAL: Record<string, MaasWorkloadType> = {
  [MaasWorkloadType.ContainerInstance]: MaasWorkloadType.ContainerInstance,
  [MaasWorkloadType.RayJob]: MaasWorkloadType.RayJob,
  [MaasWorkloadType.RayService]: MaasWorkloadType.RayService,
  containerinstance: MaasWorkloadType.ContainerInstance,
  rayjob: MaasWorkloadType.RayJob,
  rayservice: MaasWorkloadType.RayService,
};

/** 将表单或 types API 返回值规范为后端枚举（避免 containerinstance 等大小写导致校验失败） */
export function normalizeMaasRuntimeType(value?: string): MaasRuntimeType | undefined {
  const raw = String(value ?? '').trim();
  if (!raw) return undefined;
  return MAAS_RUNTIME_TYPE_CANONICAL[raw] ?? MAAS_RUNTIME_TYPE_CANONICAL[raw.toLowerCase()];
}

export function normalizeMaasWorkloadType(value?: string): MaasWorkloadType | undefined {
  const raw = String(value ?? '').trim();
  if (!raw) return undefined;
  return MAAS_WORKLOAD_TYPE_CANONICAL[raw] ?? MAAS_WORKLOAD_TYPE_CANONICAL[raw.toLowerCase()];
}

export function normalizeMaasRuntime(runtime?: MaasRuntime): MaasRuntime | undefined {
  if (!runtime) return undefined;
  const type = normalizeMaasRuntimeType(runtime.type);
  const workload = normalizeMaasWorkloadType(runtime.workload);
  if (!type || !workload) return undefined;
  return { type, workload };
}

/** 规范 types 接口返回的 runtime 选项键与 workload 列表 */
export function normalizeMaasOptions(options: MaasOptions): MaasOptions {
  const runtime: NonNullable<MaasOptions['runtime']> = {};
  Object.entries(options.runtime || {}).forEach(([rawType, workloads]) => {
    const type = normalizeMaasRuntimeType(rawType);
    if (!type) return;
    const list = (workloads || []).map((item) => normalizeMaasWorkloadType(item)).filter((item): item is MaasWorkloadType => !!item);
    if (list.length) runtime[type] = list;
  });
  return { ...options, runtime };
}
